import { createOperationDescriptor } from 'relay-runtime';
import type { IEnvironment } from 'relay-runtime';

import ConversationQueryArtifact, {
	type ObjectCommentContainerPreloadObjectCommentsQuery$variables as ConversationQuery$variables,
} from './components/__generated__/ObjectCommentContainerPreloadObjectCommentsQuery.graphql.ts';
import { ConversationQueryReference } from './ConversationQueryReference.ts';

type Entry = {
	query: ConversationQueryReference;
	readers: number;
};

/**
 * Preload-to-render handoff, scoped to the Relay environment. Only the most recent
 * unclaimed preload is retained; mounted readers keep their own entries alive.
 */
export class ConversationQueryOwner {
	private entries = new Map<string, Entry>();
	private entriesByReference = new WeakMap<ConversationQueryReference, Entry>();
	private pending: Entry | undefined;
	private hydrationCleanup: (() => void) | undefined;

	constructor(private readonly environment: IEnvironment) {}

	private notify(): void {
		listeners.get(this.environment)?.forEach((listener) => listener());
	}

	beginPreload(): void {
		this.hydrationCleanup?.();
		this.hydrationCleanup = undefined;
		this.releaseUnclaimed();
	}

	getHydrationVariables(): ConversationQuery$variables | undefined {
		const query = this.pending?.query;
		return query?.settled && !query.error && !query.disposed ? query.variables : undefined;
	}

	setHydrationCleanup(cleanup: () => void): void {
		this.hydrationCleanup?.();
		this.hydrationCleanup = cleanup;
	}

	get(variables: ConversationQuery$variables): ConversationQueryReference | undefined {
		return this.entries.get(
			createOperationDescriptor(ConversationQueryArtifact, variables).request.identifier,
		)?.query;
	}

	preload(
		variables: ConversationQuery$variables,
		fetchPolicy: 'network-only' | 'store-or-network' = 'store-or-network',
	): ConversationQueryReference {
		const existing = this.get(variables);
		if (
			existing &&
			!existing.disposed &&
			!existing.error &&
			(fetchPolicy !== 'network-only' || !existing.settled)
		)
			return existing;

		this.releaseUnclaimed();
		const query = new ConversationQueryReference(this.environment, variables, fetchPolicy);
		const entry = { query, readers: 0 };
		this.entries.set(query.identifier, entry);
		this.entriesByReference.set(query, entry);
		this.pending = entry;
		this.notify();
		return query;
	}

	retain(query: ConversationQueryReference): () => void {
		const entry = this.entriesByReference.get(query);
		if (!entry || query.disposed) {
			// A superseded render may reach its effect after its preload was cancelled.
			// It must not resurrect that execution or invalidate the current owner.
			return () => {};
		}
		entry.readers++;
		this.hydrationCleanup?.();
		this.hydrationCleanup = undefined;
		if (this.pending === entry) this.pending = undefined;
		let released = false;
		return () => {
			if (released) return;
			released = true;
			entry.readers--;
			// A Strict Mode effect replay can immediately acquire the same reference.
			queueMicrotask(() => {
				if (entry.readers === 0 && this.pending !== entry) {
					this.remove(entry);
				}
			});
		};
	}

	releaseUnclaimed(): void {
		if (this.pending && this.pending.readers === 0) {
			this.remove(this.pending);
		}
		this.pending = undefined;
	}

	private remove(entry: Entry): void {
		if (this.entries.get(entry.query.identifier) === entry) {
			this.entries.delete(entry.query.identifier);
			this.notify();
		}
		entry.query.dispose();
	}
}

const owners = new WeakMap<IEnvironment, ConversationQueryOwner>();
const listeners = new WeakMap<IEnvironment, Set<() => void>>();

/** Subscribe after commit, including when the route has not created an owner yet. */
export function subscribeConversationQueryOwner(
	environment: IEnvironment,
	listener: () => void,
): () => void {
	let subscriptions = listeners.get(environment);
	if (!subscriptions) {
		subscriptions = new Set();
		listeners.set(environment, subscriptions);
	}
	subscriptions.add(listener);
	return () => {
		subscriptions.delete(listener);
	};
}

/** Called by preload/bootstrap/event code, never during React render. */
export function getOrCreateConversationQueryOwner(
	environment: IEnvironment,
): ConversationQueryOwner {
	let owner = owners.get(environment);
	if (!owner) {
		owner = new ConversationQueryOwner(environment);
		owners.set(environment, owner);
	}
	return owner;
}

/** Pure lookup for rendering; missing owners must be prepared outside render. */
export function getConversationQueryOwner(
	environment: IEnvironment,
): ConversationQueryOwner | undefined {
	return owners.get(environment);
}
